import { reduce } from 'lodash'
import find from 'lodash/find'
import flatten from 'lodash/flatten'
import get from 'lodash/get'
import groupBy from 'lodash/groupBy'
import isEmpty from 'lodash/isEmpty'
import map from 'lodash/map'
import uniqBy from 'lodash/uniqBy'

import { getLargeImageSizedUrl } from 'utils/images'
import { getDataFromSkuId } from 'utils/transformations'

import {
  getColorVariantSwatchesInfo,
  getProductCode,
  getProductExternalMedia,
  getProductFabrication,
  getProductImages,
  getProductName,
  getProductUnitsAvailableValue,
  getProductVideos,
} from 'shop/products/getters'
import { PRODUCT_SIZE_CODE, getEmptyStyleTag } from 'shop/products/traits'
import { getVariantByCode } from 'shop/products/utils'

import {
  TAG_NAME,
  TAG_VALUE,
} from './CatalogFilters/CatalogFilters.transformations'
import { TRAITS_KEY, TRAIT_FILTER_KEYS } from './CatalogFilters/filters'

export const calculateWholesaleRange = (product) => {
  return {
    min: get(product, 'priceRange.minWholesaleValue', null),
    max: get(product, 'priceRange.maxWholesaleValue', null),
  }
}

export const getProductForCard = (
  product,
  colorUnits,
  unitsBySkuId,
  filterSoldOutColors,
  ignoresInventory = false,
) => ({
  name: getProductName(product),
  code: getProductCode(product),
  id: product.id,
  orderNbr: product.orderNbr,
  comments: product.comments,
  wholesaleRange: calculateWholesaleRange(product),
  retailRange: {
    min: get(product, 'priceRange.minRetailValue', null),
    max: get(product, 'priceRange.maxRetailValue', null),
  },
  images: getProductImages(product).map((image) =>
    getLargeImageSizedUrl(image.url),
  ),
  videos: getProductVideos(product).map((vid) => vid.url),
  externalMedia: getProductExternalMedia(product)?.map((exMedia) => ({
    type: exMedia.type,
    url: exMedia.url,
    format: exMedia.format,
    ...(exMedia.thumbnailUrl && { thumbnailUrl: exMedia.thumbnailUrl }),
    ...(exMedia?.payload && { payload: exMedia?.payload }),
  })),
  badges: product.badges,
  fabrication: getProductFabrication(product),
  swatchesInfo: getColorVariantSwatchesInfo({
    product,
    colorUnits,
    filterSoldOutColors,
    ignoresInventory,
  }),
  units: getProductUnitsAvailableValue(product, unitsBySkuId, ignoresInventory),
  hasOnlyCasepacks:
    Boolean(product.casepacks?.length) &&
    !Boolean(
      getVariantByCode(product.variants, PRODUCT_SIZE_CODE)?.values?.length,
    ),
})

export const addTotalProductsToCollection = (
  collections,
  totalProductsPerCollection,
) =>
  map(collections, ({ id: collectionId, ...rest }) => {
    const totalProducts = find(
      totalProductsPerCollection,
      (totalPerCollection) =>
        get(totalPerCollection, 'collection.id') === collectionId,
    )
    return {
      id: collectionId,
      total: get(totalProducts, 'total', 0),
      ...rest,
    }
  })

export const getUniqGroupedByTags = (collectionProducts, groupByTagInfo) =>
  uniqBy(
    collectionProducts,
    (collectionProduct) =>
      !isEmpty(get(collectionProduct, 'groupedBy')) &&
      get(collectionProduct, 'groupedBy.id'),
  ).reduce((acc, { groupedBy }) => {
    const groupBy = groupedBy || getEmptyStyleTag(groupByTagInfo.text)
    return { ...acc, [groupBy.id]: groupBy }
  }, {})

export const getInventoryBySkuId = (aggregateSkuInventoryItems) => {
  const allInventories = reduce(
    aggregateSkuInventoryItems,
    (acc, warehouseInventory) => acc.concat(warehouseInventory.items),
    [],
  )

  return reduce(
    allInventories,
    (itemsMap, item) => {
      const isPositive = item.availableQuantity > 0
      const key = isPositive ? 'positive' : 'negative'
      const previousValue = itemsMap.get(item.skuId) || {}
      if (item.availableQuantity !== undefined) {
        const quantity = item.availableQuantity + (previousValue[key] || 0)
        itemsMap.set(item.skuId, { ...previousValue, [key]: quantity })
      }
      return itemsMap
    },
    new Map(),
  )
}

export const getInventoryByVariantColor = (aggregateSkuInventoryItems) => {
  const skuInventoryItems = flatten(
    map(aggregateSkuInventoryItems, (skuAggregate) => skuAggregate.items),
  )

  return skuInventoryItems.reduce((itemsMap, item) => {
    const { color } = getDataFromSkuId(item.skuId)
    const prevQuantity = itemsMap.get(color) || 0

    itemsMap.set(color, prevQuantity + item.availableQuantity)

    return itemsMap
  }, new Map())
}

export const addAppliedTraitFiltersToTraitsFilter = (
  filters = {},
  appliedFilters,
) => {
  const traitFilters = get(filters, `[${TRAITS_KEY}]`, [])
  const newTraitFilters = reduce(
    TRAIT_FILTER_KEYS,
    (acc, { filterKey, traitCode }) => {
      const filter = get(appliedFilters, `[${filterKey}]`)
      if (isEmpty(filter)) {
        return acc
      }
      return [
        ...acc,
        {
          traitCode,
          traitValueIds: map(filter, ({ id }) => id),
        },
      ]
    },
    [],
  )
  const result = {
    ...filters,
    ...((!isEmpty(traitFilters) || !isEmpty(newTraitFilters)) && {
      [TRAITS_KEY]: [...traitFilters, ...newTraitFilters],
    }),
  }
  return result
}

export const formatStyletagFilters = (filters = {}) => {
  const { styleTagsFilter, ...rest } = filters
  if (!styleTagsFilter) return filters

  const tagIds = (
    styleTagsFilter.find((tagFilter) => tagFilter.level === TAG_NAME) || {
      values: [],
    }
  ).values
  const tagValueIds = (
    styleTagsFilter.find((tagFilter) => tagFilter.level === TAG_VALUE) || {
      values: [],
    }
  ).values

  return {
    ...rest,
    ...(tagIds.length && { tagIds }),
    ...(tagValueIds.length && { tagValueIds }),
  }
}

export const convertToVariantIdDictionary = (variantsArr) => {
  return groupBy(variantsArr, (variant) => variant?.collection?.id)
}

/**
 * Even though this says it groups by orders, it will actually
 * group by a combination of warehouse and pricetype.
 */
export const formatPriceTypeWarehouseId = ({ priceType, warehouse, door }) =>
  `${priceType?.id || '-'}:${warehouse?.id || '-'}:${door?.id || '-'}`
export const groupVariantsByOrders = (variantsInCart) => {
  return groupBy(variantsInCart, (variant) => {
    return formatPriceTypeWarehouseId({
      priceType: variant?.priceType,
      warehouse: variant?.warehouse,
      door: variant?.door,
    })
  })
}

export const formatCollectionProductId = (collection, product) =>
  `${collection?.id ?? '-'}:${product?.id ?? '-'}`
export const groupVariantsByCollectionProduct = (variantsInCart) => {
  const variantsByCollectionProduct = groupBy(variantsInCart, (variant) =>
    formatCollectionProductId(variant.collection, variant.product),
  )

  return reduce(
    variantsByCollectionProduct,
    (acc, variants, collectionProductId) => {
      return {
        ...acc,
        [collectionProductId]: groupVariantsByOrders(variants),
      }
    },
    {},
  )
}
